// GAVION manual-candidates: « Ajouter un candidat » (Indeed, a referral, other) — the field rules and the file checks
// of the form. SQL twin: public.recruiting_add_application and private.recruiting_manual_file
// (supabase/schemas/95_gavion_manual_candidates.sql). The files are read by their first bytes with gavion-api's own
// checks (cvFile.ts: PDF, Word 97–2003, Word 2007+ without macros; 2 MB); the phone with the shared NANP normaliser.
import {
  CV_CONTENT_TYPES,
  CV_MAX_BYTES,
  cleanCvFilename,
  detectCvType,
  type CvExt,
} from "../../../../supabase/functions/gavion-api/cvFile.ts";
import { normalizePhone } from "../domain/phone";

/** The sources an admin may pick (website rows come from the site only). */
export const MANUAL_SOURCES = ["indeed", "referral", "other"] as const;
export type ManualSource = (typeof MANUAL_SOURCES)[number];
export const APPLICATION_SOURCES = ["website", ...MANUAL_SOURCES] as const;
export type ApplicationSource = (typeof APPLICATION_SOURCES)[number];

/** The « Poste » choice for « Candidature spontanée » (else a posting id). */
export const SPONTANEOUS_CHOICE = "spontaneous";
export const CANDIDATE_FILE_ACCEPT = ".pdf,.doc,.docx";
/** https only, a host with a dot; twin of the RPC's profile_url rule. */
export const PROFILE_URL_PATTERN =
  /^https:\/\/[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+(:[0-9]{1,5})?([/?#]\S*)?$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type ManualCandidateForm = {
  name: string;
  /** "" (not chosen yet), SPONTANEOUS_CHOICE or a posting id. */
  posting: string;
  source: ManualSource;
  email: string;
  phone: string;
  availability: string;
  profile_url: string;
  message: string;
};
export const EMPTY_MANUAL_CANDIDATE: ManualCandidateForm = {
  name: "",
  posting: "",
  source: "indeed",
  email: "",
  phone: "",
  availability: "",
  profile_url: "",
  message: "",
};

/** The RPC's invalid_application DETAIL values. */
export type ManualField =
  | "uid"
  | "name"
  | "posting_id"
  | "source"
  | "contact"
  | "email"
  | "phone"
  | "availability"
  | "profile_url"
  | "message"
  | "cv"
  | "letter";
/** Error keys under gavion.recruiting.add.errors. */
export type ManualError =
  | "required"
  | "invalid"
  | "contact_required"
  | "too_long"
  | "file_type"
  | "file_size";
export type ManualErrors = Partial<Record<ManualField, ManualError>>;

const trim = (v: string) => v.trim();
const blankToNull = (v: string) => (v.trim() ? v.trim() : null);

/** The field errors of the form (empty: it can be sent). Files are checked when picked (checkCandidateFile). */
export function validateManualCandidate(v: ManualCandidateForm): ManualErrors {
  const e: ManualErrors = {};
  const name = trim(v.name);
  if (!name) e.name = "required";
  else if (name.length > 200) e.name = "too_long";
  if (!v.posting) e.posting_id = "required";
  if (!(MANUAL_SOURCES as readonly string[]).includes(v.source))
    e.source = "required";
  const email = trim(v.email).toLowerCase();
  const phone = trim(v.phone);
  if (!email && !phone) e.contact = "contact_required";
  if (email && (email.length > 254 || !EMAIL_PATTERN.test(email)))
    e.email = "invalid";
  if (phone && (phone.length > 40 || !normalizePhone(phone)))
    e.phone = "invalid";
  if (trim(v.availability).length > 500) e.availability = "too_long";
  const url = trim(v.profile_url);
  if (url && (url.length > 500 || !PROFILE_URL_PATTERN.test(url)))
    e.profile_url = "invalid";
  if (trim(v.message).length > 5000) e.message = "too_long";
  return e;
}

export type CandidateFile = {
  file: Blob;
  ext: CvExt;
  contentType: (typeof CV_CONTENT_TYPES)[CvExt];
  filename: string;
};
export type CandidateFileCheck =
  | { ok: true; value: CandidateFile }
  | { ok: false; error: "file_type" | "file_size" };

const extOf = (name: string) =>
  name.includes(".") ? name.slice(name.lastIndexOf(".") + 1).toLowerCase() : "";

/** A CV or letter file: 1 B – 2 MB, PDF / DOC / DOCX by its bytes, its extension the same type. */
export async function checkCandidateFile(
  file: File,
): Promise<CandidateFileCheck> {
  if (file.size === 0 || file.size > CV_MAX_BYTES)
    return { ok: false, error: "file_size" };
  const bytes = new Uint8Array(await file.arrayBuffer());
  const ext = detectCvType(bytes);
  const filename = cleanCvFilename(file.name);
  if (!ext || !filename || extOf(filename) !== ext)
    return { ok: false, error: "file_type" };
  return {
    ok: true,
    value: { file, ext, contentType: CV_CONTENT_TYPES[ext], filename },
  };
}

/** What RecruitingClient.addApplication receives. */
export type ManualCandidateInput = {
  name: string;
  posting_id: number | null;
  source: ManualSource;
  email: string | null;
  phone: string | null;
  availability: string | null;
  profile_url: string | null;
  message: string | null;
  cv: CandidateFile | null;
  letter: CandidateFile | null;
};

export const toManualInput = (
  v: ManualCandidateForm,
  cv: CandidateFile | null,
  letter: CandidateFile | null,
): ManualCandidateInput => ({
  name: trim(v.name),
  posting_id:
    v.posting && v.posting !== SPONTANEOUS_CHOICE ? Number(v.posting) : null,
  source: v.source,
  email: blankToNull(v.email)?.toLowerCase() ?? null,
  phone: blankToNull(v.phone),
  availability: blankToNull(v.availability),
  profile_url: blankToNull(v.profile_url),
  message: blankToNull(v.message),
  cv,
  letter,
});
